import { forwardRef } from "react";
import { clsx } from "@/utils";

export type TypographyVariant = "h1" | "h2" | "h3" | "title" | "action" | "paragraph" | "muted" | "small" | "small-regular" | "paragraph2";
export interface TypographyProps extends React.HTMLAttributes<HTMLHeadingElement & HTMLSpanElement> {
  variant?: TypographyVariant;
}

export const Typography = forwardRef<HTMLHeadingElement & HTMLSpanElement, TypographyProps>(
  ({ variant, className, children, ...props }, ref) => {
    const baseClass = "typography";

    switch (variant) {
      case "h1":
        return (
          <h1 className={clsx(baseClass, "text-3xl font-bold max-lg:text-xl max-md:text-2xl", className)} {...props} ref={ref}>
            {children}
          </h1>
        );
      case "h2":
        return (
          <h2 className={clsx(baseClass, "text-2xl font-bold max-lg:text-lg", className)} {...props} ref={ref}>
            {children}
          </h2>
        );
      case "h3":
        return (
          <h3 className={clsx(baseClass, "text-[22px] font-semibold max-lg:text-lg", className)} {...props} ref={ref}>
            {children}
          </h3>
        );
      case "title":
        return (
          <span className={clsx(baseClass, "text-lg font-semibold max-lg:text-lg", className)} {...props} ref={ref}>
            {children}
          </span>
        );
      case "action":
        return (
          <span className={clsx(baseClass, "text-base font-semibold", className)} {...props} ref={ref}>
            {children}
          </span>
        );
      case "muted":
        return (
          <span className={clsx(baseClass, "text-base font-light", className)} {...props} ref={ref}>
            {children}
          </span>
        );
      case "small":
        return (
          <small className={clsx(baseClass, "inline-block text-sm font-semibold", className)} {...props} ref={ref}>
            {children}
          </small>
        );
      case "small-regular":
        return (
          <small className={clsx(baseClass, "font-regular inline-block text-sm", className)} {...props} ref={ref}>
            {children}
          </small>
        );
      case "paragraph2":
        return (
          <p className={clsx(baseClass, "font-regular text-lg", className)} {...props} ref={ref}>
            {children}
          </p>
        );
      default:
        return (
          <p className={clsx(baseClass, "font-regular text-base", className)} {...props} ref={ref}>
            {children}
          </p>
        );
    }
  }
);

Typography.displayName = "Typography";
